// services/subscription.service.js

import {
    collection,
    addDoc,
    query,
    where,
    getDocs,
    updateDoc,
    doc,
    getDoc,
    Timestamp,
    orderBy,
    limit,
    writeBatch,
} from "firebase/firestore";
import { 
    ref, 
    uploadBytes, 
    getDownloadURL 
} from "firebase/storage";
import { db, storage } from "../config/firebase";
import { SUBSCRIPTION_STATES } from "../config/constants";
import { 
    subscriptionPlans, 
    calculateNextRenewalDate,
} from "../config/subscriptionPlans";

const checkExistingSubscription = async (userId) => {
    try {
        const q = query(
            collection(db, "subscriptions"),
            where("userId", "==", userId),
            where("status", "==", SUBSCRIPTION_STATES.ACTIVE),
            limit(1)
        );
        
        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;

        const subscription = snapshot.docs[0].data();
        return {
            id: snapshot.docs[0].id,
            ...subscription
        };
    } catch (error) {
        console.error("Error checking existing subscription:", error);
        return null;
    }
};

const checkExistingRequest = async (userId) => {
    try {
        const q = query(
            collection(db, "subscriptionRequests"),
            where("userId", "==", userId),
            where("status", "in", [
                SUBSCRIPTION_STATES.PENDING_ADMIN,
                SUBSCRIPTION_STATES.PENDING_PAYMENT,
                SUBSCRIPTION_STATES.PAYMENT_SUBMITTED
            ]),
            limit(1)
        );
        
        const snapshot = await getDocs(q);
        return snapshot.empty ? null : {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data()
        };
    } catch (error) {
        console.error("Error checking existing request:", error);
        return null;
    }
};

export const subscriptionService = {
    // Admin Functions
    getAllRequests: async () => {
        try {
            const q = query(
                collection(db, "subscriptionRequests"),
                where("status", "in", [
                    SUBSCRIPTION_STATES.PENDING_ADMIN,
                    SUBSCRIPTION_STATES.PENDING_PAYMENT,
                    SUBSCRIPTION_STATES.PAYMENT_SUBMITTED
                ]),
                orderBy("createdAt", "desc")
            );

            const snapshot = await getDocs(q);
            
            // Get user data for each request
            const requests = await Promise.all(
                snapshot.docs.map(async (docSnapshot) => {
                    const request = { id: docSnapshot.id, ...docSnapshot.data() };
                    const userDocRef = doc(db, "users", request.userId);
                    const userSnap = await getDoc(userDocRef);
                    
                    return {
                        ...request,
                        userData: userSnap.exists() ? userSnap.data() : null
                    };
                })
            );

            return requests;
        } catch (error) {
            console.error("Error fetching requests:", error);
            throw error;
        }
    },

    approveRequest: async (requestId) => {
        const batch = writeBatch(db);
        
        try {
            const requestRef = doc(db, "subscriptionRequests", requestId);
            const requestDoc = await getDoc(requestRef);
            const request = requestDoc.data();

            if (!request) {
                throw new Error("Request not found");
            }

            if (request.status !== SUBSCRIPTION_STATES.PENDING_ADMIN) {
                throw new Error("Invalid request status");
            }

            // Set payment due date (24 hours)
            const paymentDue = new Date();
            paymentDue.setHours(paymentDue.getHours() + 24);

            // Update request status
            batch.update(requestRef, {
                status: SUBSCRIPTION_STATES.PENDING_PAYMENT,
                approvedAt: Timestamp.now(),
                paymentDue: Timestamp.fromDate(paymentDue),
                updatedAt: Timestamp.now()
            });

            await batch.commit();
            return true;
        } catch (error) {
            console.error("Error approving request:", error);
            throw error;
        }
    },

    rejectRequest: async (requestId, reason = "") => {
        try {
            const requestRef = doc(db, "subscriptionRequests", requestId);
            await updateDoc(requestRef, {
                status: SUBSCRIPTION_STATES.REJECTED,
                rejectionReason: reason,
                updatedAt: Timestamp.now()
            });
            return true;
        } catch (error) {
            console.error("Error rejecting request:", error);
            throw error;
        }
    },

    // Client Functions
    getUserRequest: async (userId) => {
        try {
            const q = query(
                collection(db, "subscriptionRequests"),
                where("userId", "==", userId),
                where("status", "in", [
                    SUBSCRIPTION_STATES.PENDING_ADMIN,
                    SUBSCRIPTION_STATES.PENDING_PAYMENT,
                    SUBSCRIPTION_STATES.PAYMENT_SUBMITTED
                ]),
                limit(1)
            );
            
            const snapshot = await getDocs(q);
            if (snapshot.empty) return null;

            const request = snapshot.docs[0].data();
            return {
                id: snapshot.docs[0].id,
                ...request
            };
        } catch (error) {
            console.error("Error fetching user request:", error);
            throw error;
        }
    },
    
    requestSubscription: async (userId, planData) => {
        try {
            // Check for existing subscriptions and requests
            const [existingSubscription, existingRequest] = await Promise.all([
                checkExistingSubscription(userId),
                checkExistingRequest(userId)
            ]);

            if (existingSubscription?.status === SUBSCRIPTION_STATES.ACTIVE) {
                throw new Error("You already have an active subscription");
            }

            if (existingRequest) {
                throw new Error("You already have a pending subscription request");
            }

            const { planId, childCount, referralCode, ...additionalData } = planData;
            const plan = subscriptionPlans[planId];

            if (!plan) {
                throw new Error("Invalid plan selected");
            }

            // Calculate discount and prices
            let discount = 0;
            if (childCount > 1) {
                discount = childCount === 2 ? 15 : 25;
            }

            const basePrice = parseInt(plan.price.replace(/,/g, ''));
            const discountAmount = (basePrice * discount) / 100;
            const finalPrice = basePrice - discountAmount;

            const docRef = await addDoc(collection(db, "subscriptionRequests"), {
                userId,
                planId,
                childCount,
                discount,
                basePrice,
                finalPrice,
                referralCode,
                plan: {
                    name: plan.name,
                    interval: plan.interval,
                    periodLength: plan.periodLength,
                    periodCredits: plan.periodCredits,
                    features: plan.features
                },
                status: SUBSCRIPTION_STATES.PENDING_ADMIN,
                createdAt: Timestamp.now(),
                ...additionalData,
            });

            return docRef.id;
        } catch (error) {
            console.error("Error creating subscription request:", error);
            throw error;
        }
    },

    // Payment Functions
    submitPayment: async (requestId, paymentDetails, proofFile) => {
        try {
            // First, get the request details
            const requestRef = doc(db, "subscriptionRequests", requestId);
            const requestDoc = await getDoc(requestRef);
            
            if (!requestDoc.exists()) {
                throw new Error("Request not found");
            }
    
            const request = requestDoc.data();
    
            // File validations
            if (!proofFile) {
                throw new Error("Payment proof file is required");
            }
    
            const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
            if (proofFile.size > MAX_FILE_SIZE) {
                throw new Error("File size should not exceed 5MB");
            }
    
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
            if (!allowedTypes.includes(proofFile.type)) {
                throw new Error("Only JPG, PNG and PDF files are allowed");
            }
    
            // Upload file
            const filename = `payment-proofs/${request.userId}-${Date.now()}.${proofFile.name.split('.').pop()}`;
            const fileRef = ref(storage, filename);
            await uploadBytes(fileRef, proofFile);
            const proofUrl = await getDownloadURL(fileRef);
    
            // Create payment record
            const paymentRef = await addDoc(collection(db, "payments"), {
                requestId,
                userId: request.userId,
                subscriptionPlan: request.planId,
                basePrice: request.basePrice,
                finalPrice: request.finalPrice,
                discount: request.discount,
                status: 'pending_verification',
                paymentDetails: {
                    ...paymentDetails,
                    proofUrl,
                    filename,
                    originalName: proofFile.name,
                    fileType: proofFile.type,
                    fileSize: proofFile.size,
                    submittedAt: Timestamp.now()
                },
                plan: request.plan,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
    
            // Update subscription request status
            await updateDoc(requestRef, {
                status: SUBSCRIPTION_STATES.PAYMENT_SUBMITTED,
                paymentId: paymentRef.id,
                updatedAt: Timestamp.now()
            });
    
            return true;
        } catch (error) {
            console.error("Error submitting payment:", error);
            throw error;
        }
    },

    // Verify payment and activate subscription
    verifyPayment: async (paymentId) => {
        const batch = writeBatch(db);
        
        try {
            // Get the payment document
            const paymentRef = doc(db, "payments", paymentId);
            const paymentDoc = await getDoc(paymentRef);
            
            if (!paymentDoc.exists()) {
                throw new Error("Payment not found");
            }
    
            const payment = paymentDoc.data();
            
            // Get the subscription request using payment.requestId
            const requestRef = doc(db, "subscriptionRequests", payment.requestId);
            const requestDoc = await getDoc(requestRef);
            
            if (!requestDoc.exists()) {
                throw new Error("Request not found");
            }
    
            const request = requestDoc.data();
    
            // Calculate subscription dates
            const startDate = new Date();
            const nextRenewal = calculateNextRenewalDate(startDate, request.planId);
    
            // Create active subscription
            const subscriptionRef = await addDoc(collection(db, "subscriptions"), {
                userId: request.userId,
                planId: request.planId,
                childCount: request.childCount,
                plan: request.plan,
                discount: request.discount,
                basePrice: request.basePrice,
                finalPrice: request.finalPrice,
                periodCredits: request.plan.periodCredits,
                availableCredits: request.plan.periodCredits,
                rolloverCredits: 0,
                purchasedCredits: 0,
                status: SUBSCRIPTION_STATES.ACTIVE,
                startDate: Timestamp.fromDate(startDate),
                nextRenewal: Timestamp.fromDate(nextRenewal),
                paymentHistory: [{
                    date: payment.paymentDetails.submittedAt,
                    amount: request.finalPrice,
                    method: payment.paymentDetails.method,
                    proofUrl: payment.paymentDetails.proofUrl
                }],
                createdAt: Timestamp.now()
            });
    
            // Update payment status
            batch.update(paymentRef, {
                status: 'verified',
                verifiedAt: Timestamp.now(),
                subscriptionId: subscriptionRef.id,
                updatedAt: Timestamp.now()
            });
    
            // Update subscription request status
            batch.update(requestRef, {
                status: SUBSCRIPTION_STATES.ACTIVE,
                subscriptionId: subscriptionRef.id,
                activatedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
    
            // Update user document
            batch.update(doc(db, "users", request.userId), {
                hasActiveSubscription: true,
                currentSubscriptionId: subscriptionRef.id,
                subscriptionPlan: request.planId,
                updatedAt: Timestamp.now()
            });
    
            await batch.commit();
            return subscriptionRef.id;
        } catch (error) {
            console.error("Error verifying payment:", error);
            throw error;
        }
    },

    // Reject payment
    rejectPayment: async (paymentId, reason = '') => {
        const batch = writeBatch(db);
        
        try {
            // Get payment document
            const paymentRef = doc(db, "payments", paymentId);
            const paymentDoc = await getDoc(paymentRef);
            
            if (!paymentDoc.exists()) {
                throw new Error("Payment not found");
            }
    
            const payment = paymentDoc.data();
    
            // Get subscription request
            const requestRef = doc(db, "subscriptionRequests", payment.requestId);
            const requestDoc = await getDoc(requestRef);
            
            if (!requestDoc.exists()) {
                throw new Error("Request not found");
            }
    
            // Update payment status
            batch.update(paymentRef, {
                status: 'rejected',
                rejectionReason: reason,
                rejectedAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            });
    
            // Update request status back to pending payment
            batch.update(requestRef, {
                status: SUBSCRIPTION_STATES.PENDING_PAYMENT,
                updatedAt: Timestamp.now()
            });
    
            await batch.commit();
            return true;
        } catch (error) {
            console.error("Error rejecting payment:", error);
            throw error;
        }
    },

    // Get user's active subscription
    getUserSubscription: async (userId) => {
        try {
            const q = query(
                collection(db, "subscriptions"),
                where("userId", "==", userId),
                where("status", "==", SUBSCRIPTION_STATES.ACTIVE),
                limit(1)
            );
            
            const snapshot = await getDocs(q);
            if (snapshot.empty) return null;

            const subscription = {
                id: snapshot.docs[0].id,
                ...snapshot.docs[0].data()
            };

            // Calculate days until renewal
            const nextRenewal = subscription.nextRenewal.toDate();
            const today = new Date();
            const daysUntilRenewal = Math.ceil((nextRenewal - today) / (1000 * 60 * 60 * 24));

            // Calculate if plan change is allowed (within last 7 days of period)
            const canChangePlan = daysUntilRenewal <= 7;

            return {
                ...subscription,
                daysUntilRenewal,
                canChangePlan
            };
        } catch (error) {
            console.error("Error fetching subscription:", error);
            throw error;
        }
    },

    // Process subscription renewal
    processRenewal: async (subscriptionId) => {
        const batch = writeBatch(db);
        
        try {
            const subscriptionRef = doc(db, "subscriptions", subscriptionId);
            const subscriptionDoc = await getDoc(subscriptionRef);
            const subscription = subscriptionDoc.data();

            if (!subscription || subscription.status !== SUBSCRIPTION_STATES.ACTIVE) {
                throw new Error("Invalid subscription");
            }

            // Calculate new renewal date
            const nextRenewal = calculateNextRenewalDate(
                new Date(),
                subscription.planId
            );

            // Handle credit rollover
            const unusedCredits = subscription.availableCredits || 0;
            const periodCredits = subscription.periodCredits;
            const totalNewCredits = periodCredits + unusedCredits;

            // Update subscription
            batch.update(subscriptionRef, {
                availableCredits: totalNewCredits,
                rolloverCredits: unusedCredits,
                lastRenewal: Timestamp.now(),
                nextRenewal: Timestamp.fromDate(nextRenewal),
                updatedAt: Timestamp.now()
            });

            // Create renewal record
            const renewalRef = doc(collection(db, "renewals"));
            batch.set(renewalRef, {
                subscriptionId,
                userId: subscription.userId,
                renewalDate: Timestamp.now(),
                nextRenewal: Timestamp.fromDate(nextRenewal),
                periodCredits,
                rolloverCredits: unusedCredits,
                totalCredits: totalNewCredits,
                basePrice: subscription.basePrice,
                finalPrice: subscription.finalPrice
            });

            await batch.commit();
            return true;
        } catch (error) {
            console.error("Error processing renewal:", error);
            throw error;
        }
    },

    // Check if renewal is needed
    checkRenewalNeeded: async (subscriptionId) => {
        try {
            const subscriptionDoc = await getDoc(doc(db, "subscriptions", subscriptionId));
            const subscription = subscriptionDoc.data();

            if (!subscription) return false;

            const nextRenewal = subscription.nextRenewal.toDate();
            const now = new Date();

            return nextRenewal <= now;
        } catch (error) {
            console.error("Error checking renewal:", error);
            throw error;
        }
    },

    getPendingPayments: async () => {
        try {
            const q = query(
                collection(db, "payments"),
                where("status", "==", "pending_verification"),
                orderBy("createdAt", "desc")
            );
    
            const snapshot = await getDocs(q);
            
            // Get user data and request data for each payment
            const payments = await Promise.all(
                snapshot.docs.map(async (docSnapshot) => {
                    const payment = { id: docSnapshot.id, ...docSnapshot.data() };
                    
                    // Get user data
                    const userDocRef = doc(db, "users", payment.userId);
                    const userSnap = await getDoc(userDocRef);
                    
                    // Get subscription request data
                    const requestDocRef = doc(db, "subscriptionRequests", payment.requestId);
                    const requestSnap = await getDoc(requestDocRef);
                    const requestData = requestSnap.exists() ? requestSnap.data() : null;
                    
                    return {
                        ...payment,
                        userData: userSnap.exists() ? userSnap.data() : null,
                        basePrice: requestData?.basePrice || 0,
                        finalPrice: requestData?.finalPrice || 0,
                        discount: requestData?.discount || 0,
                        plan: requestData?.plan || null
                    };
                })
            );
    
            return payments;
        } catch (error) {
            console.error("Error fetching pending payments:", error);
            throw error;
        }
    }
};

export default subscriptionService;